import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import cx from 'classnames';
import { Store } from 'components/common/types/Store.types';
import IconButton from 'lib/IconButton';
import sidebarSimpleLeftIcon from 'assets/icons/sidebar-simple-left.svg';
import copyIcon from 'assets/icons/copy.svg';
import useIsReadOnly from 'components/pages/Planner/useIsReadOnly';
import usePendoTracking from 'customHooks/usePendoTracking';
import { useDuplicateDeal } from 'customHooks/useDuplicateDeal';
import { useHasDealReadOnlyPermission } from 'customHooks/useHasDealReadOnlyPermission';
import Tooltip, { TooltipDirection } from 'lib/Tooltip';
import { DealStatus, FrontEndType } from 'components/common/types/Deal.types';
import CampaignDuplicateModal from 'components/common/CampaignDuplicationModal/CampaignDuplicationModal';
import { useToggle } from 'customHooks/useToggle';
import { getEveryLineStatus } from 'store/dealManagement/selectors';
import { getIsDirectSalesCampaignType } from 'store/dealManagement/selectors/getCampaignType';
import { ActionButtonIcon } from 'components/pages/Planner/PlannerLineActions/utils/components';
import { actionButtonClassName } from 'components/pages/Planner/PlannerLineActions/utils/functions';
import { getDealId, getDisplayCampaignId } from 'store/dealManagement/selectors/getDealId';
import { getIsLoading } from 'store/dealManagement/selectors/getIsLoading';
import { requestCampaignNameFocus } from 'store/dealManagement/reducer';
import { ChipBookingStatus } from '../../../ChipBookingStatus/ChipBookingStatus';
import { IconTradingType } from '../IconTradingType/IconTradingType';
import { SelectTradingType } from '../SelectTradingType/SelectTradingType';
import { ShareCampaignPlan } from '../ShareCampaignPlan/ShareCampaignPlan';
import { UnsavedCampaignChangesModal } from '../UnsavedCampaignChangesModal/UnsavedCampaignChangesModal';
import { useCloseCampaignDrawer } from '../useCloseCampaignDrawer';
import { useIsCampaignPlanSharable } from './useIsCampaignPlanSharable';
import { getDealDisplayName } from './utils';

export const CampaignNameAndStatus: React.FC = () => {
  const dispatch = useDispatch();
  const { getPendoDataId } = usePendoTracking();

  const { handleDuplicateDeal } = useDuplicateDeal();
  const { campaignId: originalCampaignId } = useParams();

  const dealName = useSelector((state: Store) => state.dealManagement.commonDeal.dealName);
  const dealId = useSelector(getDealId);
  const displayCampaignId = useSelector(getDisplayCampaignId);
  const isCampaignDrawerOpen = useSelector((state: Store) => state.dealManagement.isCampaignDrawerOpen);
  const bookingStatusCode = useSelector((state: Store) => state.dealManagement.commonDeal.bookingStatusCode);
  const isLoading = useSelector(getIsLoading);
  const linesStates = useSelector(getEveryLineStatus);
  const bookingStatus = useSelector((state: Store) => state.dealManagement.commonDeal.bookingStatusCode);
  const isPlannerHeader =
    useSelector((state: Store) => state.dealManagement.commonDeal.currentLine.frontEndType) === FrontEndType.PLANNER;

  const { isCampaignReadOnly } = useIsReadOnly();
  const hasDealReadOnlyPermission = useHasDealReadOnlyPermission();
  const isDirectSalesCampaignType = useSelector(getIsDirectSalesCampaignType);
  const {
    isOpen: isCampaignDuplicationModalOpen,
    open: openCampaignDuplicationModal,
    close: closeCampaignDuplicationModal,
  } = useToggle();

  const enabledForSharing = useSelector((state: Store) => state.dealManagement.directSales.enabledForSharing);
  const campaignPlanShareUrl = useSelector((state: Store) => state.dealManagement.directSales.campaignPlanShareUrl);
  const isCampaignPlanSharable = useIsCampaignPlanSharable();
  const isShareCampaignPlanButtonDisabled =
    !isCampaignPlanSharable && enabledForSharing !== false && !campaignPlanShareUrl;

  const {
    isUnsavedChangesModalOpen,
    handleCloseDrawer: handleToggleCampaignDrawer,
    handleDismissModal,
    handleDiscard,
    handleSaveAndClose,
    isSaveDisabled,
  } = useCloseCampaignDrawer();

  const handleOpenDrawerForCampaignNameEdit = (): void => {
    dispatch(requestCampaignNameFocus(true));
    handleToggleCampaignDrawer();
  };

  return (
    <div
      className={cx('flex flex-shrink-0 gap-2 items-center px-[8px]', {
        'border-r border-neutral-950-opacity-10': !isCampaignDrawerOpen,
        'w-[380px]': isPlannerHeader && !isCampaignDrawerOpen,
        'w-[348px] bg-neutral-200': !isPlannerHeader || isCampaignDrawerOpen,
      })}
    >
      {isCampaignDrawerOpen ? (
        <SelectTradingType />
      ) : (
        <>
          <IconTradingType />
          <Tooltip tooltip="Edit campaign name" direction={TooltipDirection.BOTTOM}>
            <button
              data-test-id="deal-header-deal-name"
              className={cx('header-base w-[271px] max-w-full truncate text-left', {
                'text-essential-tertiary italic': !dealName && !dealId,
                'text-essential-primary': !!dealName || !!dealId,
              })}
              type="button"
              onClick={handleOpenDrawerForCampaignNameEdit}
              disabled={isCampaignReadOnly}
            >
              {getDealDisplayName(dealName, dealId, originalCampaignId, displayCampaignId)}
            </button>
          </Tooltip>
          <ChipBookingStatus status={bookingStatus} />
        </>
      )}

      <div className="flex items-center justify-center gap-1">
        {!!bookingStatusCode && isCampaignDrawerOpen && !hasDealReadOnlyPermission && (
          <IconButton
            icon={copyIcon}
            name="Duplicate campaign"
            dataPendoId={getPendoDataId('Duplicate campaign')}
            handleOnClick={() => {
              handleDuplicateDeal(dealId);
            }}
            isDisabled={isLoading || bookingStatusCode === DealStatus.FAILED}
          />
        )}
        {isDirectSalesCampaignType && !hasDealReadOnlyPermission && (
          <Tooltip tooltip="Duplicate campaign" direction={TooltipDirection.BOTTOM}>
            <button
              data-test-id="duplicate-campaign-button"
              className={actionButtonClassName}
              type="button"
              aria-label="Duplicate campaign"
              onClick={openCampaignDuplicationModal}
              disabled={!dealId}
            >
              {ActionButtonIcon(copyIcon)}
            </button>
          </Tooltip>
        )}

        <ShareCampaignPlan isDisabled={isShareCampaignPlanButtonDisabled} />

        <Tooltip tooltip="Campaign info" direction={TooltipDirection.BOTTOM}>
          <IconButton icon={sidebarSimpleLeftIcon} name="Campaign info" handleOnClick={handleToggleCampaignDrawer} />
        </Tooltip>
      </div>
      {isDirectSalesCampaignType && (
        <CampaignDuplicateModal
          isOpen={isCampaignDuplicationModalOpen}
          onClose={closeCampaignDuplicationModal}
          dealLineStates={linesStates}
          campaignId={dealId}
        />
      )}
      {isDirectSalesCampaignType && (
        <UnsavedCampaignChangesModal
          isOpen={isUnsavedChangesModalOpen}
          onDismiss={handleDismissModal}
          onDiscard={handleDiscard}
          onSave={handleSaveAndClose}
          isSaveDisabled={isSaveDisabled}
        />
      )}
    </div>
  );
};
